import { accessToken, redirectToLogin, SignedOut, withRetry } from "./auth";

export const API_URL = import.meta.env.VITE_API_URL as string;
export const ACCOUNTS_URL = import.meta.env.VITE_ACCOUNTS_URL as string;

export type FieldError = { field: string; code: string; message?: string };

// ApiError is an RFC 9457 problem response.
export class ApiError extends Error {
  status: number;
  code: string;
  errors: FieldError[];
  body: Record<string, unknown>; // the whole problem (extension members, e.g. a conflict's saved content)
  constructor(status: number, body: { code?: string; detail?: string; title?: string; errors?: FieldError[] }) {
    super(body.detail || body.title || `Request failed (${status})`);
    this.status = status;
    this.code = body.code ?? "error";
    this.errors = body.errors ?? [];
    this.body = body as Record<string, unknown>;
  }
}

type AuthPromptCode = "step_up_required" | "mfa_required";
const authPromptListeners = new Set<(code: AuthPromptCode, returnTo?: string) => void>();

export function onAuthPrompt(fn: (code: AuthPromptCode, returnTo?: string) => void) {
  authPromptListeners.add(fn);
  return () => { authPromptListeners.delete(fn); };
}

// isAuthPrompt: errors already answered by the security dialog (callers
// should not toast them too).
export const isAuthPrompt = (e: unknown) => e instanceof ApiError && (e.code === "step_up_required" || e.code === "mfa_required");

// authPrompt: false leaves step-up / MFA errors to the caller (no dialog).
type Options = { method?: string; body?: unknown; signal?: AbortSignal; headers?: Record<string, string>; authPrompt?: boolean };

// newKey makes an Idempotency-Key for stock- and money-moving requests.
export const newKey = () => crypto.randomUUID();

export async function api<T = unknown>(path: string, opts: Options = {}): Promise<T> {
  const method = opts.method ?? (opts.body === undefined ? "GET" : "POST");
  const send = async (force: boolean) => {
    let tok: string;
    try {
      tok = await accessToken(force);
    } catch (e) {
      if (e instanceof SignedOut) redirectToLogin();
      throw e;
    }
    const doFetch = () => fetch(API_URL + path, {
      method,
      headers: {
        // The UI language: server-rendered text (notifications) follows it.
        "Accept-Language": document.documentElement.lang || "en",
        Authorization: `Bearer ${tok}`,
        ...(opts.body !== undefined ? { "Content-Type": "application/json" } : {}),
        ...opts.headers,
      },
      body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
      signal: opts.signal,
    });
    // Reads (and requests carrying an Idempotency-Key) are safe to retry
    // while the api restarts; other writes are not retried automatically.
    return method === "GET" || opts.headers?.["Idempotency-Key"] ? withRetry(doFetch) : doFetch();
  };
  const read = async (res: Response) => {
    const text = await res.text();
    return text ? JSON.parse(text) : {};
  };
  let res = await send(false);
  let body = res.status === 204 ? {} : await read(res);
  // 401 means an expired/revoked token, except step-up (recent MFA needed).
  if (res.status === 401 && body.code !== "step_up_required") {
    res = await send(true);
    body = res.status === 204 ? {} : await read(res);
    if (res.status === 401 && body.code !== "step_up_required") redirectToLogin();
  }
  if (res.status === 204) return undefined as T;
  if (!res.ok) {
    const err = new ApiError(res.status, body);
    // Sensitive actions: the app shows a dialog to re-authenticate (or turn
    // on two-step verification) and come back here.
    if (isAuthPrompt(err) && opts.authPrompt !== false) authPromptListeners.forEach((fn) => fn(err.code as AuthPromptCode));
    throw err;
  }
  return body as T;
}

// requireStepUp is for sensitive forms: it resolves true when a recent
// second-factor sign-in has at least minSeconds left to fill the form in.
// Otherwise it shows the security dialog, which verifies and comes back to
// returnTo, and resolves false.
export async function requireStepUp(returnTo: string, minSeconds = 180): Promise<boolean> {
  let code: AuthPromptCode = "step_up_required";
  try {
    const r = await api<{ valid_for: number }>("/admin/v1/me/step-up", { authPrompt: false });
    if (r.valid_for >= minSeconds) return true;
  } catch (e) {
    if (!isAuthPrompt(e)) throw e;
    code = (e as ApiError).code as AuthPromptCode;
  }
  authPromptListeners.forEach((fn) => fn(code, returnTo));
  return false;
}

export const storePath = (storeId: string, path = "") => `/admin/v1/stores/${storeId}${path}`;

// download fetches an authenticated file (CSV, PDF) and saves it.
export async function download(path: string, fallbackName: string) {
  const res = await fetch(API_URL + path, { headers: { Authorization: `Bearer ${await accessToken()}` } });
  if (!res.ok) {
    const text = await res.text();
    throw new ApiError(res.status, text ? JSON.parse(text) : {});
  }
  const name = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(res.headers.get("Content-Disposition") ?? "")?.[1];
  const url = URL.createObjectURL(await res.blob());
  const a = Object.assign(document.createElement("a"), { href: url, download: name ? decodeURIComponent(name) : fallbackName });
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// openAuthed opens an authenticated page (a printable invoice) in a new tab.
export async function openAuthed(path: string) {
  const tab = window.open("", "_blank");
  const res = await fetch(API_URL + path, { headers: { Authorization: `Bearer ${await accessToken()}` } });
  if (!res.ok) {
    tab?.close();
    const text = await res.text();
    throw new ApiError(res.status, text ? JSON.parse(text) : {});
  }
  const url = URL.createObjectURL(new Blob([await res.text()], { type: "text/html" }));
  if (tab) tab.location.href = url;
  else window.location.href = url;
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
